// Detects whether a pasted video link can be played inline, and how.
//
// - YouTube and Vimeo both officially support iframe embedding (it's what
//   their own "Share > Embed" buttons generate), so those play inline.
// - A URL that points straight at a video file (.mp4, .webm, .ogg, .mov)
//   can be played with a plain <video> tag.
// - Anything else (Instagram, TikTok, a generic webpage, etc.) can't be
//   reliably embedded without that platform's own SDK, and most block
//   being framed at all — so those fall back to "open the original link"
//   instead of trying to force an embed that would likely just fail.

import { getYoutubeVideoId } from "./youtube";

const VIMEO_HOSTS = ["vimeo.com", "www.vimeo.com", "player.vimeo.com"];
const VIDEO_FILE_EXTENSIONS = [".mp4", ".webm", ".ogg", ".ogv", ".mov"];

const getVimeoVideoId = (url) => {
  try {
    const parsed = new URL(url);
    if (!VIMEO_HOSTS.includes(parsed.hostname)) {
      return null;
    }
    const match = parsed.pathname.match(/(\d+)/);
    return match ? match[1] : null;
  } catch {
    return null;
  }
};

export const isDirectVideoFile = (url) => {
  try {
    const parsed = new URL(url);
    const path = parsed.pathname.toLowerCase();
    return VIDEO_FILE_EXTENSIONS.some((ext) => path.endsWith(ext));
  } catch {
    return false;
  }
};

// Vimeo doesn't expose a predictable thumbnail URL like YouTube does, so
// getting a cover image requires asking Vimeo's own oEmbed endpoint for it.
export const getVimeoThumbnail = async (url) => {
  const vimeoId = getVimeoVideoId(url);
  if (!vimeoId) return null;
  try {
    const res = await fetch(
      `https://vimeo.com/api/oembed.json?url=${encodeURIComponent(url)}`
    );
    if (!res.ok) return null;
    const data = await res.json();
    return data.thumbnail_url || null;
  } catch {
    return null;
  }
};

// Returns one of:
//   { kind: "youtube", embedUrl }
//   { kind: "vimeo", embedUrl }
//   { kind: "direct", url }
//   null  (not embeddable — caller should link out instead)
export const getEmbeddableVideo = (url) => {
  const youtubeId = getYoutubeVideoId(url);
  if (youtubeId) {
    return {
      kind: "youtube",
      embedUrl: `https://www.youtube.com/embed/${youtubeId}?autoplay=1`,
    };
  }

  const vimeoId = getVimeoVideoId(url);
  if (vimeoId) {
    return {
      kind: "vimeo",
      embedUrl: `https://player.vimeo.com/video/${vimeoId}?autoplay=1`,
    };
  }

  if (isDirectVideoFile(url)) {
    return { kind: "direct", url };
  }

  return null;
};
